<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="openMenu">
            <ion-icon :icon="menuOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>События</ion-title>
        <ion-buttons slot="end" v-if="isAdmin">
          <ion-button @click="$router.push('/admin-events')">
            <ion-icon :icon="createOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-refresher slot="fixed" @ionRefresh="load($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <div class="events-scope-wrap ion-padding-horizontal ion-padding-top">
        <ion-segment
          mode="ios"
          v-model="eventScope"
          @ionChange="onScopeChange"
          class="events-scope-segment"
        >
          <ion-segment-button mode="ios" value="upcoming">
            <ion-label>Предстоящие</ion-label>
          </ion-segment-button>
          <ion-segment-button mode="ios" value="past">
            <ion-label>Прошедшие</ion-label>
          </ion-segment-button>
          <ion-segment-button v-if="isAdmin" mode="ios" value="all">
            <ion-label>Все</ion-label>
          </ion-segment-button>
        </ion-segment>
      </div>
      <div class="ion-padding">
        <p v-if="loading" class="ion-text-center">Загрузка...</p>
        <p v-else-if="events.length === 0" class="ion-text-center">{{ emptyListMessage }}</p>
        <ion-list v-else lines="full">
          <ion-item
            v-for="ev in events"
            :key="ev.id"
            button
            @click="$router.push(`/events/${ev.id}`)"
            class="event-item"
          >
            <img
              :src="ev.logoPath ? eventsService.logoUrl(ev.logoPath) : defaultEventLogo"
              slot="start"
              class="event-logo"
              :alt="ev.title"
            />
            <ion-label>
              <h2>{{ ev.title }}</h2>
              <p>{{ formatDate(ev.startAt) }} · {{ ev.location || 'Место уточняется' }}</p>
              <p class="event-badges">
                <span v-if="ev.isPaid" class="event-paid">Платное</span>
                <span v-if="isSoldOut(ev)" class="event-soldout">Sold out</span>
                <span v-if="registrationsBadge(ev)" class="event-count">{{ registrationsBadge(ev) }}</span>
              </p>
            </ion-label>
          </ion-item>
        </ion-list>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar
} from '@ionic/vue';
import { menuController } from '@ionic/vue';
import { createOutline, menuOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import { ILigaEvent } from '@/model/liga-event.model';
import eventsService from '@/services/events.service';
import dayjs from 'dayjs';

export default defineComponent({
  name: 'EventsList',
  setup() {
    const accountService = inject<{ hasAnyAuthorityAndCheckAuth: (auth: string) => Promise<boolean> }>('accountService');
    return { accountService };
  },
  components: {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar
  },
  computed: {
    emptyListMessage(): string {
      if (this.eventScope === 'past') return 'Нет прошедших событий.';
      if (this.eventScope === 'all') return 'Нет событий.';
      return 'Нет предстоящих событий.';
    }
  },
  data() {
    return {
      events: [] as ILigaEvent[],
      loading: true,
      isAdmin: false,
      /** upcoming | past | all (только админ) */
      eventScope: 'upcoming' as 'upcoming' | 'past' | 'all',
      headerScrolled: false,
      eventsService,
      defaultEventLogo: require('@/theme/liga-logo-white.png'),
      createOutline,
      menuOutline
    };
  },
  async mounted() {
    this.isAdmin = await this.accountService?.hasAnyAuthorityAndCheckAuth?.('ROLE_ADMIN') ||
      await this.accountService?.hasAnyAuthorityAndCheckAuth?.('ROLE_MANAGER') || false;
    await this.load();
  },
  ionViewWillEnter() {
    this.load();
  },
  methods: {
    isSoldOut(ev: ILigaEvent): boolean {
      if (ev.maxRegistrations == null) return false;
      const count = ev.registrationsCount ?? 0;
      return count >= ev.maxRegistrations;
    },
    /** Сколько всего записалось (без лимита мест). */
    registrationsBadge(ev: ILigaEvent): string {
      return String(ev.registrationsCount ?? 0);
    },
    openMenu() {
      menuController.open('mainMenu');
    },
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    onScopeChange() {
      if (!this.isAdmin && this.eventScope === 'all') {
        this.eventScope = 'upcoming';
        return;
      }
      this.load();
    },
    async load(event?: CustomEvent) {
      this.loading = true;
      try {
        let opts: boolean | { all?: boolean; past?: boolean };
        if (this.isAdmin && this.eventScope === 'all') {
          opts = true;
        } else if (this.eventScope === 'past') {
          opts = { past: true };
        } else {
          opts = false;
        }
        this.events = await eventsService.getEvents(opts);
      } catch (e) {
        this.events = [];
      }
      this.loading = false;
      (event?.target as any)?.complete?.();
    },
    formatDate(iso: string) {
      return dayjs(iso).format('D MMMM YYYY, HH:mm');
    }
  }
});
</script>

<style scoped>
.event-logo {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 12px;
}
.event-icon {
  font-size: 32px;
  margin-right: 12px;
  color: var(--ion-color-orange);
}
.event-badges {
  margin: 0.25rem 0 0;
  font-size: 12px;
}
.event-paid {
  color: var(--ion-color-warning-shade);
}
.event-soldout {
  color: #dc2f02;
  margin-left: 0.5rem;
}
.event-count {
  color: rgba(255, 255, 255, 0.85);
  margin-left: 0.5rem;
}
.event-item {
  --padding-start: 12px;
  --background: rgba(42, 42, 42, 0.6);
  --color: #fff;
  --border-color: rgba(255, 255, 255, 0.1);
}
.event-item h2,
.event-item p {
  color: #fff;
}
/* Одна стрелка «подробнее» — встроенная в ion-item; цвет под тёмный фон */
.event-item {
  --detail-icon-color: #fff;
}
.events-scope-wrap {
  padding-bottom: 8px;
}
.events-scope-segment {
  --background: rgba(42, 42, 42, 0.85);
  max-width: 100%;
}
.events-scope-segment ion-segment-button {
  --color: rgba(255, 255, 255, 0.65);
  --color-checked: #fff;
  --indicator-color: var(--ion-color-orange);
  min-width: 0;
  font-size: 0.8rem;
}
</style>
